import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../contexts/UserStoreContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

const InvoiceDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentInvoice, loading, error, getInvoiceDetails, payInvoice, getInvoiceStatus } = useUserStore();
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(null);

    useEffect(() => {
        if (id) {
            getInvoiceDetails(id);
        }
    }, [id, getInvoiceDetails]);

    // Polling for status updates if not paid
    useEffect(() => {
        let interval;
        if (id && currentInvoice && currentInvoice.status !== 'paid' && currentInvoice.status !== 'canceled') {
            interval = setInterval(() => {
                getInvoiceStatus(id);
            }, 5000); // Poll every 5 seconds
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [id, currentInvoice, getInvoiceStatus]);

    const handlePay = async () => {
        setPaying(true);
        setPayError(null);
        try {
            const successUrl = `${window.location.origin}/payment/success`;
            const cancelUrl = `${window.location.origin}/payment/cancel`;

            const { data, error } = await payInvoice(id, {
                success_url: successUrl,
                cancel_url: cancelUrl
            });

            if (error) {
                setPayError(error.message || 'Payment failed');
            } else if (data?.url) {
                window.location.href = data.url;
            } else {
                // If no redirect URL, it was a direct payment, so go to success
                navigate('/payment/success');
            }
        } catch (err) {
            setPayError('An unexpected error occurred during payment.');
        } finally {
            setPaying(false);
        }
    };

    if (loading && !currentInvoice) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            </div>
        );
    }

    if (error || !currentInvoice) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <div className="flex-1 flex flex-col items-center justify-center p-4">
                    <div className="bg-destructive/10 text-destructive p-6 rounded-xl max-w-md w-full text-center">
                        <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">Invoice Not Found</h2>
                        <p className="mb-6">{error || 'The requested invoice could not be found.'}</p>
                        <Button onClick={() => navigate('/invoices')}>
                            Back to Invoices
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const invoiceStatus = (currentInvoice.status || '').toLowerCase();
    const isPaid = invoiceStatus === 'paid';
    const isCanceled = invoiceStatus === 'canceled';
    const canPayInvoice = !isPaid && !isCanceled;

    const getStatusSteps = () => {
        const steps = [
            { id: 'draft', label: 'Draft', icon: 'FileText' },
            { id: 'sent', label: 'Sent', icon: 'Send' },
            { id: 'active', label: 'Active', icon: 'Clock' },
            { id: 'paid', label: 'Paid', icon: 'CheckCircle' }
        ];

        const currentStatus = currentInvoice.status.toLowerCase();
        let activeIndex = steps.findIndex(s => s.id === currentStatus);
        if (currentStatus === 'overdue') activeIndex = 1; // Still sent but overdue
        if (currentStatus === 'canceled') return [{ id: 'canceled', label: 'Canceled', icon: 'XCircle', color: 'text-destructive' }];

        return steps.map((step, index) => ({
            ...step,
            completed: index <= activeIndex,
            active: index === activeIndex
        }));
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Header />
            <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-8 flex items-center justify-between">
                        <button
                            onClick={() => navigate('/invoices')}
                            className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Icon name="ChevronLeft" size={16} className="mr-1" />
                            Back to Invoices
                        </button>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium border ${isPaid
                            ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                            {currentInvoice.status.toUpperCase()}
                        </div>
                    </div>

                    {/* Status Timeline */}
                    <div className="bg-card border border-border rounded-2xl p-6 mb-8 shadow-sm">
                        <div className="flex items-center justify-between relative">
                            {/* Connector Line */}
                            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-muted -translate-y-1/2 z-0"></div>

                            {getStatusSteps().map((step, index) => (
                                <div key={step.id} className="relative z-10 flex flex-col items-center">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${step.completed
                                        ? 'bg-primary border-primary text-primary-foreground'
                                        : 'bg-card border-muted text-muted-foreground'
                                        } ${step.active ? 'ring-4 ring-primary/20' : ''}`}>
                                        <Icon name={step.icon} size={18} />
                                    </div>
                                    <span className={`mt-2 text-xs font-bold uppercase tracking-wider ${step.completed ? 'text-primary' : 'text-muted-foreground'
                                        }`}>
                                        {step.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl shadow-elevation-2 overflow-hidden">
                        {/* Invoice Header */}
                        <div className="p-8 border-b border-border bg-muted/30">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div>
                                    <h1 className="text-3xl font-bold text-foreground mb-2">Invoice</h1>
                                    <p className="text-muted-foreground">#{currentInvoice.invoice_number || currentInvoice.id.substring(0, 8)}</p>
                                </div>
                                <div className="text-left md:text-right">
                                    <p className="text-sm text-muted-foreground mb-1">Issue Date</p>
                                    <p className="text-lg font-semibold text-foreground">
                                        {new Date(currentInvoice.issue_date || currentInvoice.date).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Body */}
                        <div className="p-8">
                            <div className="mb-8">
                                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Description</h3>
                                <div className="bg-muted/20 rounded-xl p-4 border border-border">
                                    <p className="text-foreground font-medium">{currentInvoice.description || 'Subscription Upgrade'}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Billing Details</h3>
                                    <div className="space-y-2">
                                        <p className="text-foreground font-medium">MessagingHub Pro User</p>
                                        <p className="text-sm text-muted-foreground">Tenant ID: {currentInvoice.tenant_id}</p>
                                    </div>
                                </div>
                                <div className="bg-primary/5 rounded-xl p-6 border border-primary/10">
                                    <h3 className="text-sm font-medium text-primary uppercase tracking-wider mb-2">Total Amount</h3>
                                    <p className="text-4xl font-black text-foreground">
                                        {currentInvoice.currency || '$'} {(currentInvoice.amount / 100).toFixed(2)}
                                    </p>
                                </div>
                            </div>

                            {payError && (
                                <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-lg flex flex-col space-y-1">
                                    <div className="flex items-center space-x-2">
                                        <Icon name="AlertCircle" size={20} />
                                        <span className="font-bold">Payment Error</span>
                                    </div>
                                    <p className="text-sm ml-7">{payError}</p>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
                                {canPayInvoice ? (
                                    <Button
                                        variant="default"
                                        size="lg"
                                        fullWidth
                                        onClick={handlePay}
                                        disabled={paying}
                                        className="h-12 text-lg font-bold"
                                    >
                                        {paying ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                <span>Processing Payment...</span>
                                            </div>
                                        ) : (
                                            <>
                                                <Icon name="CreditCard" size={20} className="mr-2" />
                                                Pay Now
                                            </>
                                        )}
                                    </Button>
                                ) : isPaid ? (
                                    <div className="w-full bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 rounded-xl p-4 flex items-center justify-center text-green-700 dark:text-green-400 font-bold">
                                        <Icon name="CheckCircle" size={24} className="mr-2" />
                                        This invoice has been paid
                                    </div>
                                ) : (
                                    <div className="w-full bg-muted/30 border border-border rounded-xl p-4 flex items-center justify-center text-muted-foreground font-bold">
                                        <Icon name="XCircle" size={24} className="mr-2" />
                                        This invoice has been canceled and can no longer be paid
                                    </div>
                                )}
                                <Button
                                    variant="outline"
                                    size="lg"
                                    fullWidth
                                    onClick={() => window.print()}
                                    className="h-12"
                                >
                                    <Icon name="Download" size={20} className="mr-2" />
                                    Download PDF
                                </Button>
                            </div>
                        </div>

                        {/* Invoice Footer */}
                        <div className="p-8 bg-muted/30 border-t border-border text-center">
                            <p className="text-sm text-muted-foreground">
                                Thank you for choosing MessagingHub Pro. If you have any questions about this invoice, please contact our support team.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetails;
